import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import Cookies from 'js-cookie';

import { User, AuthTokens } from '@/types/auth';
import { authService } from '@/services/auth';
import { logger } from '@/utils/logger';

interface AuthState {
  // State
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  register: (data: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  updateUser: (userData: Partial<User>) => void;
  checkAuth: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
}

// Token management utilities
const TOKEN_KEY = 'auth_tokens';
const REFRESH_TOKEN_KEY = 'refresh_token';

const getStoredTokens = (): AuthTokens | null => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
    
    if (accessToken && refreshToken) {
      return {
        accessToken,
        refreshToken,
      };
    }
    return null;
  } catch (error) {
    logger.error('Failed to get stored tokens:', error);
    return null;
  }
};

const storeTokens = (tokens: AuthTokens, remember: boolean = false) => {
  try {
    localStorage.setItem('access_token', tokens.accessToken);
    
    // Store refresh token in httpOnly cookie for security
    Cookies.set(REFRESH_TOKEN_KEY, tokens.refreshToken, {
      expires: remember ? 30 : undefined, // 30 days if remember me
      httpOnly: false, // Note: httpOnly should be true in production with proper backend setup
      secure: import.meta.env.PROD,
      sameSite: 'strict',
    });
  } catch (error) {
    logger.error('Failed to store tokens:', error);
  }
};

const clearStoredTokens = () => {
  try {
    localStorage.removeItem('access_token');
    Cookies.remove(REFRESH_TOKEN_KEY);
  } catch (error) {
    logger.error('Failed to clear stored tokens:', error);
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true, // 초기값을 true로 설정하여 인증 체크 완료까지 대기
      error: null,

      // Actions
      login: async (email: string, password: string, remember = false) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await authService.login({ email, password });
          const { user, tokens } = response;

          // Store tokens
          storeTokens(tokens, remember);

          set({
            user,
            tokens,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          logger.info('User logged in successfully');
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Login failed';
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          
          logger.error('Login failed:', error);
          throw error;
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null });
        
        try {
          await authService.register(data);
          
          set({
            isLoading: false,
            error: null,
          });

          logger.info('User registered successfully');
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Registration failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          logger.error('Registration failed:', error);
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        
        try {
          const { tokens } = get();
          if (tokens?.refreshToken) {
            await authService.logout(tokens.refreshToken);
          }
        } catch (error) {
          logger.error('Logout API call failed:', error);
          // Continue with local logout even if API call fails
        }

        // Clear stored tokens and state
        clearStoredTokens();
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });

        logger.info('User logged out successfully');
      },

      refreshToken: async () => {
        const { tokens } = get();
        
        if (!tokens?.refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const response = await authService.refreshToken(tokens.refreshToken);
          const newTokens = response.tokens;

          // Update stored tokens
          storeTokens(newTokens);

          set({
            tokens: newTokens,
          });

          logger.info('Token refreshed successfully');
        } catch (error) {
          logger.error('Token refresh failed:', error);
          
          // Force logout if refresh fails
          get().logout();
          throw error;
        }
      },

      checkAuth: async () => {
        const state = get();
        
        // 이미 인증 체크가 진행 중이면 중복 실행 방지
        if (state.isLoading && state.user !== null) {
          return;
        }
        
        set({ isLoading: true });
        
        // 최대 10초 후 강제로 로딩 완료
        const timeout = setTimeout(() => {
          logger.warn('Auth check timeout - proceeding without authentication');
          set({ 
            isLoading: false,
            isAuthenticated: false,
            user: null,
            tokens: null,
            error: null
          });
        }, 10000);
        
        try {
          // Try to get tokens from storage
          const storedTokens = getStoredTokens();
          
          if (!storedTokens) {
            clearTimeout(timeout);
            set({ 
              isLoading: false,
              isAuthenticated: false,
              user: null,
              tokens: null,
              error: null
            });
            return;
          }

          // 이미 유저 정보가 있고 토큰이 유효하면 API 호출 스킵
          if (state.user && state.isAuthenticated) {
            clearTimeout(timeout);
            set({ isLoading: false });
            return;
          }

          // API 호출 시 타임아웃 설정 (5초)
          const controller = new AbortController();
          const apiTimeout = setTimeout(() => controller.abort(), 5000);

          try {
            // Verify token and get user info
            const response = await authService.getProfile(controller.signal);
            const user = response.user;

            clearTimeout(apiTimeout);
            clearTimeout(timeout);

            set({
              user,
              tokens: storedTokens,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            logger.info('Auth check successful');
          } catch (apiError: any) {
            clearTimeout(apiTimeout);
            
            // 네트워크 오류인 경우 토큰이 있다면 캐시된 사용자 정보 사용
            if (apiError.name === 'AbortError' || apiError.code === 'NETWORK_ERROR') {
              if (state.user) {
                logger.warn('Network error during auth check - using cached user info');
                clearTimeout(timeout);
                set({
                  isLoading: false,
                  isAuthenticated: true,
                  error: null,
                });
                return;
              }
            }
            
            throw apiError; // 다른 에러는 외부 catch로 전달
          }
          
        } catch (error: any) {
          clearTimeout(timeout);
          logger.error('Auth check failed:', error);
          
          // 401 에러만 토큰 클리어 (네트워크 에러는 토큰 유지)
          if (error.response?.status === 401 || error.response?.status === 403) {
            clearStoredTokens();
          }
          
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      forgotPassword: async (email: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await authService.forgotPassword(email);
          
          set({
            isLoading: false,
            error: null,
          });

          logger.info('Password reset email sent');
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Failed to send reset email';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          logger.error('Forgot password failed:', error);
          throw error;
        }
      },

      resetPassword: async (token: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await authService.resetPassword(token, password);
          
          set({
            isLoading: false,
            error: null,
          });

          logger.info('Password reset successfully');
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Password reset failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          logger.error('Reset password failed:', error);
          throw error;
        }
      },

      verifyEmail: async (token: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await authService.verifyEmail(token);
          
          // Update user's email_verified status if user is logged in
          const { user } = get();
          if (user) {
            set({
              user: { ...user, email_verified: true },
            });
          }

          set({
            isLoading: false,
            error: null,
          });

          logger.info('Email verified successfully');
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Email verification failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          logger.error('Email verification failed:', error);
          throw error;
        }
      },

      resendVerificationEmail: async () => {
        set({ isLoading: true, error: null });
        
        try {
          await authService.resendVerificationEmail();
          
          set({
            isLoading: false,
            error: null,
          });

          logger.info('Verification email sent successfully');
        } catch (error: any) {
          const errorMessage = error.response?.data?.error || 'Failed to send verification email';
          set({
            isLoading: false,
            error: errorMessage,
          });
          
          logger.error('Resend verification email failed:', error);
          throw error;
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({
            user: { ...user, ...userData },
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
      onRehydrateStorage: () => (state) => {
        // 저장된 상태가 복원되면 즉시 로딩 완료로 설정
        if (state) {
          state.isLoading = false;
          logger.info('Auth state rehydrated from storage');
        }
      },
    }
  )
);

// Auto-refresh token before expiration
let refreshInterval: NodeJS.Timeout;

const startTokenRefreshInterval = () => {
  clearInterval(refreshInterval);
  
  refreshInterval = setInterval(async () => {
    const { tokens, isAuthenticated, refreshToken } = useAuthStore.getState();
    
    if (!isAuthenticated || !tokens) {
      return;
    }

    // Check if token expires in the next 5 minutes
    const now = new Date().getTime();
    const expiresAt = new Date(tokens.expiresAt).getTime();
    const fiveMinutes = 5 * 60 * 1000;

    if (expiresAt - now <= fiveMinutes) {
      try {
        await refreshToken();
      } catch (error) {
        logger.error('Auto token refresh failed:', error);
      }
    }
  }, 60 * 1000); // Check every minute
};

// Start interval when store is created
if (typeof window !== 'undefined') {
  startTokenRefreshInterval();
  
  // Remove automatic checkAuth - let AuthContext handle it
  // useAuthStore.getState().checkAuth();
}